import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { countUnread, markAsReadByBusinessId } from '@/api/business-reminder'
// eslint-disable-next-line no-duplicate-imports
import type { CountUnreadVO } from '@/api/business-reminder'
import { BusinessReminderType, RouteToBusinessTypesMap } from '@/enums/business-reminder'

/**
 * 业务提醒状态管理 Store
 *
 * 功能：
 * - 管理所有业务类型的未读数量
 * - 提供菜单未读数量查询
 * - 提供业务数据未读数量查询
 * - 处理标记已读操作
 */
export const useBusinessReminderStore = defineStore('businessReminder', () => {
  // ==================== 状态 ====================

  /** 未读数量数据 */
  const unreadData = ref<CountUnreadVO[]>([])

  /** 是否正在加载 */
  const loading = ref(false)

  /** 最后更新时间 */
  const lastUpdateTime = ref<number>(0)

  // ==================== 计算属性 ====================

  /**
   * 获取所有未读总数
   */
  const totalUnreadCount = computed(() => {
    return unreadData.value.reduce((sum, item) => sum + item.totalUnreadCount, 0)
  })

  // ==================== 方法 ====================

  /**
   * 获取未读数量数据
   * @param force 是否强制刷新（忽略缓存）
   */
  const fetchUnreadCount = async(force = false) => {
    // 如果不是强制刷新，且距离上次更新不到5秒，则不重复请求
    const now = Date.now()
    if (!force && lastUpdateTime.value && (now - lastUpdateTime.value) < 5000) {
      console.log('[BUSINESS_REMINDER] 使用缓存数据，距离上次更新:', now - lastUpdateTime.value, 'ms')
      return
    }

    loading.value = true
    try {
      console.log('[BUSINESS_REMINDER] 开始获取未读数量')
      const response = await countUnread()
      unreadData.value = response || []
      lastUpdateTime.value = now
      console.log('[BUSINESS_REMINDER] 未读数量获取成功:', unreadData.value)
    } catch (error) {
      console.error('[BUSINESS_REMINDER] 获取未读数量失败:', error)
      unreadData.value = []
    } finally {
      loading.value = false
    }
  }

  /**
   * 根据路由名称获取菜单未读数量
   * @param routeName 路由名称
   * @returns 未读数量
   */
  const getMenuUnreadCount = (routeName: string): number => {
    // 查找该路由对应的业务类型列表
    const businessTypes = RouteToBusinessTypesMap[routeName]

    if (!businessTypes || businessTypes.length === 0) {
      return 0
    }

    // 累加所有业务类型的未读数量
    return businessTypes.reduce((sum, businessType) => {
      const data = unreadData.value.find(item => item.businessType === businessType)
      return sum + (data?.totalUnreadCount || 0)
    }, 0)
  }

  /**
   * 根据业务ID获取未读数量
   * @param businessId 业务ID
   * @param businessType 业务类型
   * @returns 未读数量
   */
  const getBusinessUnreadCount = (businessId: string, businessType: BusinessReminderType): number => {
    const data = unreadData.value.find(item => item.businessType === businessType)
    if (!data || !data.businessIdUnreadMap) {
      return 0
    }
    return data.businessIdUnreadMap[businessId] || 0
  }

  /**
   * 标记业务为已读
   * @param businessType 业务类型
   * @param businessId 业务ID
   */
  const markAsRead = async(businessType: BusinessReminderType, businessId: string) => {
    try {
      console.log('[BUSINESS_REMINDER] 标记已读:', { businessType, businessId })
      await markAsReadByBusinessId({ businessType, businessId })

      // 标记成功后立即刷新未读数量
      await fetchUnreadCount(true)

      console.log('[BUSINESS_REMINDER] 标记已读成功')
    } catch (error) {
      console.error('[BUSINESS_REMINDER] 标记已读失败:', error)
      throw error
    }
  }

  /**
   * 获取指定菜单及其所有子菜单的未读总数
   * @param routeNames 路由名称数组（包含当前菜单和所有子菜单）
   * @returns 未读总数
   */
  const getMenuGroupUnreadCount = (routeNames: string[]): number => {
    return routeNames.reduce((sum, routeName) => {
      return sum + getMenuUnreadCount(routeName)
    }, 0)
  }

  /**
   * 重置状态
   */
  const reset = () => {
    unreadData.value = []
    loading.value = false
    lastUpdateTime.value = 0
  }

  return {
    // 状态
    unreadData,
    loading,
    lastUpdateTime,

    // 计算属性
    totalUnreadCount,

    // 方法
    fetchUnreadCount,
    getMenuUnreadCount,
    getBusinessUnreadCount,
    getMenuGroupUnreadCount,
    markAsRead,
    reset
  }
})
